import React, { FC, useState, useMemo, useEffect } from 'react';
import { Button, Checkbox, Icon, Bubble } from 'tea-component';
import { RevenueFilterFiled, RevenueValue } from '../../types';
import { RevenueGroupList, DefaultRevenueValue } from '../../_config/constant';
import filterSvg from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/filter.svg';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { useIntl, getLocale } from 'umi';

import { cloneDeep, reduce } from 'lodash-es';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';

interface RevenueSelectProps {
  value: RevenueValue;
  onChange: (value: RevenueValue, type: 'reset' | 'confirm') => void;
}

const RevenueSelector: FC<RevenueSelectProps> = (props) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const { value, onChange } = props;
  /** 是否展开收起 */
  const [isExpand, setIsExpand] = useState(true);

  const [revenueValue, setRevenueValue] =
    useState<RevenueValue>(DefaultRevenueValue);

  const handleGroupChange = (value: string[], filed: RevenueFilterFiled) => {
    const copyValue = cloneDeep(revenueValue);

    copyValue[filed] = value;
    setRevenueValue(copyValue);
  };

  useEffect(() => {
    if (value) setRevenueValue(value);
  }, [value]);

  const isDisabled = useMemo(() => {
    // 每个筛选品类checkBox至少选一个
    const resultList = reduce(
      revenueValue,
      (result, value) => {
        result.push(!!value.length);
        return result;
      },
      [] as boolean[],
    );
    return resultList.some((item) => !item);
  }, [revenueValue]);

  const collapseList = useMemo(() => {
    return reduce(
      revenueValue,
      (result, value, key) => {
        const targetRevenue = RevenueGroupList.find(
          (item) => item.value === key,
        );
        if (targetRevenue) {
          const nameList = value.map((item) => {
            const targetOption = targetRevenue?.option?.find(
              (config) => config.value === item,
            );
            return formatMessage({id: targetOption?.label || ''});
          });
          result.push({
            label: formatMessage({id: targetRevenue?.name || ''}),
            value: nameList.join(', '),
          });
        }
        return result;
      },
      [] as { label: string; value: string }[],
    );
  }, [revenueValue, locale]);

  const revenueGroupList = useMemo(()=> {
    return RevenueGroupList?.map(item=> {
      return {
        ...item,
        option: item?.option?.map(config=> ({...config, label: formatMessage({id: config?.label})})),
        name: formatMessage({id: item?.name || ''}),
      }
    })

  }, [RevenueGroupList, locale])

  /** Confirm */
  const handleConfirm = () => {
    onChange && onChange(revenueValue, 'confirm');
    const btnMsg = dbiBtnLogMes?.pcConsoleBenchmarkUSDigitalConfirm;
    btnMsg && buriedBtnPoint(btnMsg);
  };

  /** 重置 */
  const handleReset = () => {
    setRevenueValue(DefaultRevenueValue);
    onChange && onChange(DefaultRevenueValue, 'reset');
    const btnMsg = dbiBtnLogMes?.pcConsoleBenchmarkUSDigitalReset;
    btnMsg && buriedBtnPoint(btnMsg);
  };

  return (
    <div className="benchmark-revenue-selector">
      {isExpand ? (
        <>
          {revenueGroupList.map((item, index) =>
            item.value === 'time_interval' ? null : (
              <div key={index} className="benchmark-revenue-selector-group">
                <div className="benchmark-revenue-selector-group-label">
                  {item.name}
                </div>
                <div className="benchmark-revenue-selector-group-option">
                  <Checkbox.Group
                    value={revenueValue[item.value]}
                    onChange={(value) => handleGroupChange(value, item.value)}
                  >
                    {item.option.map((config, num) => (
                      <Checkbox name={config.value} key={num}>
                        {config.label}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </div>
              </div>
            ),
          )}
          <div className="benchmark-revenue-selector-footer">
            <div>
              <Button
                type="primary"
                className="mr20"
                disabled={isDisabled}
                onClick={handleConfirm}
              >
                { formatMessage({id: 'dbi_confirm'})}
              </Button>
              <Button onClick={handleReset}>{formatMessage({id: 'dbi_reset'})}</Button>
            </div>
            <span className="show-selector" onClick={() => setIsExpand(false)}>
              <span>{ locale === 'zh-CN'? '关闭': 'Close'} </span>
              <SvgIcon type="point-top" className="show-selector-icon" />
            </span>
          </div>
        </>
      ) : (
        <div className="benchmark-revenue-selector-shutting">
          <div className="collapse-content">
            <span className="collapse-content-filter">
              <img src={filterSvg} alt="" />
              <span>{formatMessage({id: 'dbi_benchmark_my_filter'})}</span>
            </span>
            <div className="collapse-content-filed">
              {collapseList?.map((item, index) =>
                item.label === 'Time interval' ? null : (
                  <span key={index} className="filed-item">
                    <span className="filed-item-label">{item?.label}</span>
                    <span className="filed-item-value">
                      <Bubble content={item?.value} placement="top-start" dark>
                        {item?.value}
                      </Bubble>
                    </span>
                  </span>
                ),
              )}
            </div>
          </div>
          <span className="show-selector" onClick={() => setIsExpand(true)}>
            <span>{ locale === 'zh-CN'? '展开': 'Show'} </span>
            <SvgIcon type="point-down" className="show-selector-icon" />
          </span>
        </div>
      )}
    </div>
  );
};

RevenueSelector.displayName = 'RevenueSelect';

RevenueSelector.defaultProps = {
  value: DefaultRevenueValue,
};

export default RevenueSelector;
